import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { isAgency, isIndividual } from '@rma/generic/domain/helpers';
import { INDIVIDUAL, type ProfileType } from '@rma/generic/domain/types';
import { PagedList } from '@rma/public/generic/domain/types';
import { ExternalReview, ExternalReviewsFilter, ProfileService } from '@rma/public/profiles/private/domain';
import { map, Observable, switchMap, tap } from 'rxjs';

export interface ExternalReviewsState {
  loading: boolean;
  profileType: ProfileType;
  profileCode: string;
  skip: number;
  take: number;
  total: number;
  reviews: ExternalReview[];
  error?: string;
}

const PAGE_SIZE = 3;

@Injectable()
export class ExternalReviewsStore extends ComponentStore<ExternalReviewsState> {
  private readonly profileService = inject(ProfileService);

  private readonly profileType$ = this.select((state) => state.profileType);
  private readonly profileCode$ = this.select((state) => state.profileCode);
  private readonly loading$ = this.select((state) => state.loading);
  private readonly error$ = this.select((state) => state.error);
  private readonly reviews$ = this.select((state) => state.reviews);
  private readonly skip$ = this.select((state) => state.skip);
  private readonly take$ = this.select((state) => state.take);
  private readonly total$ = this.select((state) => state.total);
  private readonly noResults$ = this.select(this.total$, this.loading$, (total, loading) => total === 0 && !loading);

  private readonly request$ = this.select(
    {
      profileCode: this.profileCode$,
      profileType: this.profileType$,
      skip: this.skip$,
      take: this.take$,
    },
    { debounce: true },
  );

  readonly vm$ = this.select(
    {
      loading: this.loading$,
      reviews: this.reviews$,
      noResults: this.noResults$,
      skip: this.skip$,
      take: this.take$,
      total: this.total$,
      error: this.error$,
    },
    { debounce: true },
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private readonly getExternalReviewsEffect = this.effect((request$: Observable<ExternalReviewsFilter>) =>
    request$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap((filter) =>
        this.profileService.getExternalReviews(filter).pipe(
          tapResponse({
            next: (response) => this.setResults(response, filter.skip),
            error: (error: HttpErrorResponse) => {
              console.error(error);
              this.patchState(() => ({
                error: error?.error?.message ?? error.message ?? 'internal error',
                loading: false,
              }));
            },
          }),
        ),
      ),
    ),
  );

  constructor() {
    super({
      loading: true,
      profileType: INDIVIDUAL,
      profileCode: '',
      skip: 0,
      take: PAGE_SIZE,
      total: 0,
      reviews: [],
    });

    const request$: Observable<ExternalReviewsFilter> = this.request$.pipe(
      map(({ profileType, profileCode, skip, take }) => {
        const request: ExternalReviewsFilter = {
          agencyCode: isAgency(profileType) ? profileCode : undefined,
          agentCodes: isIndividual(profileType) ? [profileCode] : undefined,
          skip,
          take,
        };
        return request;
      }),
    );

    this.getExternalReviewsEffect(request$);
  }

  setProfile(profileType: ProfileType, profileCode: string) {
    this.patchState({ profileType, profileCode });
  }

  setResults(results: PagedList<ExternalReview>, skip: number) {
    const { total, results: reviews } = results;

    if (skip > 0) {
      this.patchState((state) => ({
        reviews: [...state.reviews, ...reviews],
        loading: false,
        error: undefined,
      }));
      return;
    }

    this.patchState({ reviews, skip, total, loading: false, error: undefined });
  }

  showMore(skip: number) {
    this.patchState({ skip, take: PAGE_SIZE });
  }
}
